import { GlossNumber } from "@bitwarden/web-vault/app/models/data/shared/gloss-number";
import { NormalizedPair } from "@bitwarden/web-vault/app/models/data/shared/normalized-pair";
import { GlossBalanceUtils } from "@bitwarden/web-vault/app/models/data/utils/glossBalance.utils";
import { TransactionDirection } from "@bitwarden/web-vault/app/models/enum/transactionDirection";

export interface IGlossBalance {
  normalized: number;
  // TODO: remove
  currencyBalances: Record<string, number>;
  // VALUES FOR THIS TRANSACTION OR GRANULARITY INTERVAL KEYED ON SYMBOL
  in: Record<string, NormalizedPair>;
  out: Record<string, NormalizedPair>;
  value: Record<string, NormalizedPair>;
  // RUNNING BALANCES FOR THIS GRANULARITY INTERVAL KEYED ON SYMBOL
  runningTotalIn: Record<string, NormalizedPair>;
  runningTotalOut: Record<string, NormalizedPair>;
  runningTotalValue: Record<string, NormalizedPair>;
  // RUNNING NORMALIZED TOTALS FOR THIS GRANULARITY INTERVAL
  runningTotalNormalizedIn: GlossNumber;
  runningTotalNormalizedOut: GlossNumber;
  runningTotalNormalizedValue: GlossNumber;
}

/**
 * GlossBalance - This object is used to represent the values that relate to different
 *                granularities, their running summations split by symbols.
 *                The in, out and value parameters can represent a single transaction
 *                or it can relate to an entire granularity such as a day, month or year.
 *                The runningTotalIn, runningTotalOut and runningTotalValue parameters
 *                are used to record the balance including the values in the in, out and value
 *                for this GlossBalance object.
 *                The runningTotalNormalizedIn, runningTotalNormalizedOut and runningTotalNormalizedValue
 *                parameters represent the summation of all symbols up to and including this GroupedBalance.
 *
 */

export class GlossBalance extends GlossBalanceUtils implements IGlossBalance {
  // TODO: remove
  private _normalized = 0;

  currencyBalances: Record<string, number> = {};

  in: Record<string, NormalizedPair> = {};
  out: Record<string, NormalizedPair> = {};
  value: Record<string, NormalizedPair> = {}; // combines in and out together

  runningTotalIn: Record<string, NormalizedPair> = {};
  runningTotalOut: Record<string, NormalizedPair> = {};
  runningTotalValue: Record<string, NormalizedPair> = {}; // combines in and out together

  runningTotalNormalizedIn: GlossNumber = new GlossNumber();
  runningTotalNormalizedOut: GlossNumber = new GlossNumber();
  runningTotalNormalizedValue: GlossNumber = new GlossNumber(); // combines in and out together

  // TODO: remove
  get normalized(): number {
    return this._normalized;
  }

  // TODO: remove
  set normalized(value: number) {
    this._normalized = value;
  }

  /*
  add(balance: GlossBalance) {
    if (!isNaN(balance.normalized)) {
      this.normalized += balance.normalized;
    }
    if (balance.currencyBalances) {
      // go through each
      const currencies = this.getCurrencies();
      const newCurrencies = balance.getCurrencies();
      for (const currency of newCurrencies) {
        if (currencies.includes(currency)) {
          this.currencyBalances[currency] =
            this.currencyBalances[currency] + balance.currencyBalances[currency];
        } else {
          this.currencyBalances[currency] = balance.currencyBalances[currency];
        }
      }
    }
  }
   */

  setToBalanceObj(response: Record<string, any>) {
    return response instanceof GlossBalance
      ? this.setFromObject(response)
      : this.setFromQueryString(response);
  }

  private setFromObject(balance: GlossBalance) {
    return balance;
  }

  private setFromQueryString(response: Record<string, any>) {
    // convert the record to be a glossBalance from existing pieces
    if (typeof response === "object") {
      if (response._in && typeof response._in === "object") {
        this.processNormalizedPairResponseRecord(this.in, response._in);
      } else if (response.in && typeof response.in === "object") {
        this.processNormalizedPairResponseRecord(this.in, response.in);
      }
      if (response._out && typeof response._out === "object") {
        this.processNormalizedPairResponseRecord(this.out, response._out);
      } else if (response.out && typeof response.out === "object") {
        this.processNormalizedPairResponseRecord(this.out, response.out);
      }
      if (response._value && typeof response._value === "object") {
        this.processNormalizedPairResponseRecord(this.value, response._value);
      } else if (response.value && typeof response.value === "object") {
        this.processNormalizedPairResponseRecord(this.value, response.value);
      }
      if (response._runningTotalIn && typeof response._runningTotalIn === "object") {
        this.processNormalizedPairResponseRecord(this.runningTotalIn, response._runningTotalIn);
      } else if (response.runningTotalIn && typeof response.runningTotalIn === "object") {
        this.processNormalizedPairResponseRecord(this.runningTotalIn, response.runningTotalIn);
      }
      if (response._runningTotalOut && typeof response._runningTotalOut === "object") {
        this.processNormalizedPairResponseRecord(this.runningTotalOut, response._runningTotalOut);
      } else if (response.runningTotalOut && typeof response.runningTotalOut === "object") {
        this.processNormalizedPairResponseRecord(this.runningTotalOut, response.runningTotalOut);
      }
      if (response._runningTotalValue && typeof response._runningTotalValue === "object") {
        this.processNormalizedPairResponseRecord(
          this.runningTotalValue,
          response._runningTotalValue,
        );
      } else if (response.runningTotalValue && typeof response.runningTotalValue === "object") {
        this.processNormalizedPairResponseRecord(
          this.runningTotalValue,
          response.runningTotalValue,
        );
      }
      if (
        response._runningTotalNormalizedIn &&
        typeof response._runningTotalNormalizedIn === "object"
      ) {
        this.runningTotalNormalizedIn = new GlossNumber().setToGlossNumberObj(
          response._runningTotalNormalizedIn,
        );
      } else if (
        response.runningTotalNormalizedIn &&
        typeof response.runningTotalNormalizedIn === "object"
      ) {
        this.runningTotalNormalizedIn = new GlossNumber().setToGlossNumberObj(
          response.runningTotalNormalizedIn,
        );
      }
      if (
        response._runningTotalNormalizedOut &&
        typeof response._runningTotalNormalizedOut === "object"
      ) {
        this.runningTotalNormalizedOut = new GlossNumber().setToGlossNumberObj(
          response._runningTotalNormalizedOut,
        );
      } else if (
        response.runningTotalNormalizedOut &&
        typeof response.runningTotalNormalizedOut === "object"
      ) {
        this.runningTotalNormalizedOut = new GlossNumber().setToGlossNumberObj(
          response.runningTotalNormalizedOut,
        );
      }
      if (
        response._runningTotalNormalizedValue &&
        typeof response._runningTotalNormalizedValue === "object"
      ) {
        this.runningTotalNormalizedValue = new GlossNumber().setToGlossNumberObj(
          response._runningTotalNormalizedValue,
        );
      } else if (
        response.runningTotalNormalizedValue &&
        typeof response.runningTotalNormalizedValue === "object"
      ) {
        this.runningTotalNormalizedValue = new GlossNumber().setToGlossNumberObj(
          response.runningTotalNormalizedValue,
        );
      }
    }
    // if glossBalance was not on the response then we need to wait for setGlossBalance to
    // be called on the Transaction after the quantity and valuation have been correctly configured
    return this;
  }

  private processNormalizedPairResponseRecord(
    glossBalanceProp: Record<string, NormalizedPair>,
    responseRecord: Record<string, any>,
  ) {
    for (const symbol in responseRecord) {
      const symbolInResponse = responseRecord[symbol];
      glossBalanceProp[symbol] = new NormalizedPair().setToNormalizedPairObj(symbolInResponse);
    }
  }

  private getCurrencies() {
    return Object.keys(this.currencyBalances);
  }

  /**
   * Helper Functions for GlossBalance
   */
  add(additionalBalance: GlossBalance) {
    this.addGranularityBalances(additionalBalance);
    this.addRunningBalances(additionalBalance);
  }

  addGranularityBalances(additionalBalance: GlossBalance) {
    // add any matching symbols for the current granularity interval
    GlossBalance.addToNormalizedPairRecords(this.in, additionalBalance.in);
    GlossBalance.addToNormalizedPairRecords(this.out, additionalBalance.out);
    GlossBalance.addToNormalizedPairRecords(this.value, additionalBalance.value);
  }

  addRunningBalances(additionalBalance: GlossBalance) {
    // add any matching symbols for the running totals
    GlossBalance.addToNormalizedPairRecords(this.runningTotalIn, additionalBalance.runningTotalIn);
    GlossBalance.addToNormalizedPairRecords(
      this.runningTotalOut,
      additionalBalance.runningTotalOut,
    );
    GlossBalance.addToNormalizedPairRecords(
      this.runningTotalValue,
      additionalBalance.runningTotalValue,
    );

    // Copy the normalized symbols if they are not set on this GlossBalance
    this.copyNormalizedSymbol(additionalBalance);

    // add normalized glossNumber to the normalized running total
    this.runningTotalNormalizedIn.add(additionalBalance.runningTotalNormalizedIn);
    this.runningTotalNormalizedOut.add(additionalBalance.runningTotalNormalizedOut);
    this.runningTotalNormalizedValue.add(additionalBalance.runningTotalNormalizedValue);
  }

  /**
   * If this is a new Gloss Balance, we can call this function to copy the normalized symbol across
   * from the object we are adding from.
   * @param additionalBalance
   */
  copyNormalizedSymbol(additionalBalance: GlossBalance) {
    if (!this.runningTotalNormalizedIn.symbol) {
      this.runningTotalNormalizedIn.symbol = additionalBalance.runningTotalNormalizedValue.symbol;
    }
    if (!this.runningTotalNormalizedOut.symbol) {
      this.runningTotalNormalizedOut.symbol = additionalBalance.runningTotalNormalizedValue.symbol;
    }
    if (!this.runningTotalNormalizedValue.symbol) {
      this.runningTotalNormalizedValue.symbol =
        additionalBalance.runningTotalNormalizedValue.symbol;
    }
  }

  // TODO: build out these functions
  getDirectionBySymbolAmount(direction: TransactionDirection, symbol: string): GlossNumber {
    return;
  }

  getSymbolAmount(symbol: string): GlossNumber {
    return this.runningTotalValue[symbol]?.symbolAmount;
  }

  getRunningDirectionBySymbol(direction: TransactionDirection, symbol: string): GlossNumber {
    return;
  }

  getRunningTotalSymbol(symbol: string): NormalizedPair {
    return this.runningTotalValue[symbol] ? this.runningTotalValue[symbol] : null;
  }

  getRunningNormalizedDirection(direction: TransactionDirection): GlossNumber {
    return;
  }

  getActualAmountOfSymbol(symbol: string): number {
    return this.getSymbolAmount(symbol)?.amount;
  }
}
